// S46: Admin Treasury / Company Ledger Page
import { useState, useEffect } from 'react';
import {
  Alert,
  Row,
  Col,
  Card,
  Statistic,
  Table,
  DatePicker,
  Button,
  Modal,
  Form,
  InputNumber,
  Input,
  Select,
  message,
  Tag,
  Space,
  Typography,
} from 'antd';
import {
  DollarOutlined,
  BankOutlined,
  TeamOutlined,
  LineChartOutlined,
  PlusOutlined,
  ReloadOutlined,
  WarningOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../components/PageHeader';
import CompanyBankAccountsSection from './CompanyBankAccountsSection';
import {
  adminTreasuryEndpoints,
  type TreasurySummary,
  type CompanyLedgerEntry,
  type CompanyLedgerEntriesResponse,
} from '../api/adminTreasuryEndpoints';
import { companyPaymentsEndpoints } from '../api/companyPaymentsEndpoints';

const { RangePicker } = DatePicker;
const { Text } = Typography;
const { TextArea } = Input;

export function AdminTreasury() {
  const { t } = useTranslation('treasury');
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<TreasurySummary | null>(null);
  const [ledgerEntries, setLedgerEntries] = useState<CompanyLedgerEntry[]>([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [adjustmentModalVisible, setAdjustmentModalVisible] = useState(false);
  const [hasCompanyLinkedAccount, setHasCompanyLinkedAccount] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchSummary();
    fetchLedgerEntries(1, 10);
    fetchCompanyPaymentProfile();
  }, []);  const fetchSummary = async () => {
    try {
      const data = await adminTreasuryEndpoints.getSummary();
      setSummary(data);
    } catch (error: any) {
      console.error('Failed to fetch treasury summary:', error);
      message.error(t('errors.fetchSummary'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyPaymentProfile = async () => {
    try {
      const data = await companyPaymentsEndpoints.getCompanyProfile();
      setHasCompanyLinkedAccount(data.has_linked_account);
    } catch (error: any) {
      console.error('Failed to fetch company payment profile:', error);
    }
  };

  const fetchLedgerEntries = async (
    page: number,
    pageSize: number,
    startDate?: string,
    endDate?: string
  ) => {
    setEntriesLoading(true);
    try {
      const data: CompanyLedgerEntriesResponse = await adminTreasuryEndpoints.getLedgerEntries({
        page,
        per: pageSize,
        start_date: startDate,
        end_date: endDate,
      });
      setLedgerEntries(data.entries);
      setPagination({
        current: data.pagination.page,
        pageSize: data.pagination.per_page,
        total: data.pagination.total_count,
      });
    } catch (error: any) {
      console.error('Failed to fetch ledger entries:', error);
      message.error(t('errors.fetchLedger'));
    } finally {
      setEntriesLoading(false);
    }
  };

  const handleTableChange = (newPagination: any) => {
    const startDate = dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined;
    const endDate = dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined;
    fetchLedgerEntries(newPagination.current, newPagination.pageSize, startDate, endDate);
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates) {
      setDateRange([null, null]);
      fetchLedgerEntries(1, pagination.pageSize);
      return;
    }
    setDateRange(dates);
    const startDate = dates[0] ? dates[0].format('YYYY-MM-DD') : undefined;
    const endDate = dates[1] ? dates[1].format('YYYY-MM-DD') : undefined;
    fetchLedgerEntries(1, pagination.pageSize, startDate, endDate);
  };

  const handleCreateAdjustment = async (values: any) => {
    try {
      // Convert dollars to cents
      let amount_cents = Math.round(values.amount_cents * 100);
      
      // Company sweep is always a withdrawal (negative)
      if (values.kind === 'company_sweep' && amount_cents > 0) {
        amount_cents = -amount_cents;
      }
      
      await adminTreasuryEndpoints.createAdjustment({
        amount_cents: amount_cents,
        description: values.description,
        kind: values.kind,
      });
      message.success(t('adjustment.success'));
      form.resetFields();
      setAdjustmentModalVisible(false);
      fetchSummary();
      fetchLedgerEntries(pagination.current, pagination.pageSize);
    } catch (error: any) {
      console.error('Failed to create adjustment:', error);
      message.error(t('errors.createAdjustment'));
    }
  };

  const formatCurrency = (cents: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(cents / 100);
  };

  const getKindTag = (kind: string) => {
    const kindColors: Record<string, string> = {
      company_cut: 'green',
      fee_adjustment: 'blue',
      bank_transfer_out: 'red',
      bank_transfer_in: 'cyan',
      withdrawal_paid: 'orange',
      correction: 'purple',
      manual_adjustment: 'default',
    };
    return <Tag color={kindColors[kind] || 'default'}>{t(`ledger.kinds.${kind}`)}</Tag>;
  };

  const ledgerColumns: ColumnsType<CompanyLedgerEntry> = [
    {
      title: t('ledger.columns.date'),
      dataIndex: 'occurred_at',
      key: 'occurred_at',
      width: 180,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: t('ledger.columns.kind'),
      dataIndex: 'kind',
      key: 'kind',
      width: 180,
      render: (kind: string) => getKindTag(kind),
    },
    {
      title: t('ledger.columns.description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: t('ledger.columns.amount'),
      dataIndex: 'amount_cents',
      key: 'amount_cents',
      width: 150,
      align: 'right',
      render: (amount: number) => (
        <Text strong style={{ color: amount >= 0 ? '#52c41a' : '#ff4d4f' }}>
          {amount >= 0 ? '+' : ''}{formatCurrency(amount)}
        </Text>
      ),
    },
    {
      title: t('ledger.columns.balance'),
      dataIndex: 'balance_after_cents',
      key: 'balance_after_cents',
      width: 150,
      align: 'right',
      render: (balance: number) => <Text strong>{formatCurrency(balance)}</Text>,
    },
    {
      title: t('ledger.columns.createdBy'),
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: 150,
    },
  ];

  const poolColumns: ColumnsType<any> = [
    {
      title: t('pool.columns.rank'),
      key: 'rank',
      width: 80,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: t('pool.columns.name'),
      dataIndex: 'full_name',
      key: 'full_name',
      width: 200,
      ellipsis: true,
    },
    {
      title: t('pool.columns.base'),
      dataIndex: 'base_principal_cents',
      key: 'base_principal_cents',
      width: 150,
      align: 'right',
      render: (amount: number) => formatCurrency(amount || 0),
    },
    {
      title: t('pool.columns.withdrawable'),
      dataIndex: 'withdrawable_cents',
      key: 'withdrawable_cents',
      width: 150,
      align: 'right',
      render: (amount: number) => formatCurrency(amount || 0),
    },
    {
      title: t('pool.columns.profitWeek'),
      dataIndex: 'profit_week_cents',
      key: 'profit_week_cents',
      width: 120,
      align: 'right',
      render: (amount: number) => formatCurrency(amount || 0),
    },
    {
      title: t('pool.columns.oneTime'),
      dataIndex: 'one_time_withdrawable_amount_cents',
      key: 'one_time_withdrawable_amount_cents',
      width: 120,
      align: 'right',
      render: (amount: number) => formatCurrency(amount || 0),
    },
  ];

  const getSuggestedTransferColor = () => {
    if (!summary?.liquidity_planning.suggested_transfer_to_bank_cents) return undefined;
    const amount = summary.liquidity_planning.suggested_transfer_to_bank_cents;
    if (amount > 0) return '#52c41a'; // green - transfer in
    if (amount < 0) return '#ff4d4f'; // red - transfer out
    return undefined;
  };

  return (
    <div>
      <PageHeader
        title={t('header.title')}
        extra={
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setAdjustmentModalVisible(true)}
            >
              {t('adjustment.button')}
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                fetchSummary();
                fetchLedgerEntries(pagination.current, pagination.pageSize);
              }}
            >
              {t('header.refresh')}
            </Button>
          </Space>
        }
      />

      {/* S-B3: Company Bank Account Connection */}
      <CompanyBankAccountsSection
        hasLinkedAccount={hasCompanyLinkedAccount}
        onAccountLinked={fetchCompanyPaymentProfile}
      />

      {/* S-E2: Company Payments Readiness Banner */}
      {summary?.company_payments_readiness && !summary.company_payments_readiness.ready && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message="Company Payments Not Ready"
          description={
            <Space orientation="vertical" size={4}>
              <div>On-platform deposits and payouts are currently disabled:</div>
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                {summary.company_payments_readiness.messages.map((msg, idx) => (
                  <li key={idx}>{msg}</li>
                ))}
              </ul>
            </Space>
          }
          style={{ marginBottom: 24 }}
        />
      )}

      {/* S-E4: Liquidity Warning Banner */}
      {summary?.liquidity_planning && !summary.liquidity_planning.liquidity_healthy && (
        <Alert
          type={summary.liquidity_planning.available_liquidity_cents < 0 ? 'error' : 'warning'}
          showIcon
          icon={<WarningOutlined />}
          message={summary.liquidity_planning.available_liquidity_cents < 0 ? 'Insufficient Liquidity' : 'Low Liquidity Warning'}
          description={
            <Space orientation="vertical" size={8}>
              <div>
                <strong>Available Liquidity:</strong> {formatCurrency(summary.liquidity_planning.available_liquidity_cents)}
              </div>
              <div>
                <strong>Company Balance:</strong> {formatCurrency(summary.liquidity_planning.company_available_cents)}
                {' - '}
                <strong>Pending Payouts:</strong> {formatCurrency(summary.liquidity_planning.pending_payouts_cents)}
                {' - '}
                <strong>Approved Withdrawals:</strong> {formatCurrency(summary.liquidity_planning.approved_pending_withdrawals_cents)}
              </div>
              {summary.liquidity_planning.recommended_transfer_cents > 0 && (
                <div style={{ marginTop: 8 }}>
                  <strong>Recommended Transfer:</strong> {formatCurrency(summary.liquidity_planning.recommended_transfer_cents)}
                  {' '}(to reach healthy threshold)
                </div>
              )}
            </Space>
          }
          style={{ marginBottom: 24 }}
        />
      )}

      {/* KPIs Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={8}>
          <Card loading={loading}>
            <Statistic
              title={t('kpis.totalBase')}
              value={summary?.kpis.total_base_cents || 0}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card loading={loading}>
            <Statistic
              title={t('kpis.totalWithdrawable')}
              value={summary?.kpis.total_withdrawable_cents || 0}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card loading={loading}>
            <Statistic
              title={t('kpis.totalUnsettledProfit')}
              value={summary?.kpis.unsettled_profit_cents || 0}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<LineChartOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card loading={loading}>
            <Statistic
              title={t('kpis.companyBalance')}
              value={summary?.kpis.company_balance_cents || 0}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<BankOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card loading={loading}>
            <Statistic
              title={t('kpis.totalPoolValue')}
              value={summary?.kpis.total_pool_value_cents || 0}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#52c41a', fontWeight: 'bold' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('kpis.poolValueDescription')}
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card loading={loading}>
            <Statistic
              title={t('kpis.activeUsers')}
              value={summary?.kpis.active_user_count || 0}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* S47: Company Float Tracking KPIs */}
      <Card
        title="Company Float Tracking"
        style={{ marginBottom: 24 }}
        loading={loading}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Statistic
              title="Company Float (In-Pool)"
              value={summary?.kpis.company_float_cents || 0}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<BankOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Company funds invested in pool
            </Text>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Statistic
              title="Company Withdrawable"
              value={summary?.kpis.company_withdrawable_cents || 0}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<BankOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Earmarked to sweep out
            </Text>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Statistic
              title="Float Cap Amount"
              value={summary?.kpis.company_float_cap_amount_cents || 0}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<LineChartOutlined />}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {((summary?.kpis.company_float_cap_pct || 0.2) * 100).toFixed(0)}% of user pool value
            </Text>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Statistic
              title="Subsidy Rate"
              value={summary?.kpis.effective_fee_subsidy_rate_pct || 0}
              formatter={(value) => `${Number(value).toFixed(2)}%`}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Effective fee reduction estimate
            </Text>
          </Col>
        </Row>
      </Card>

      {/* Liquidity Planning Card */}
      <Card
        title={t('liquidity.title')}
        style={{ marginBottom: 24 }}
        loading={loading}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Statistic
              title={t('liquidity.pendingWithdrawals')}
              value={summary?.liquidity_planning.approved_pending_withdrawals_cents || 0}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<WarningOutlined style={{ color: '#faad14' }} />}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Statistic
              title={t('liquidity.projectedSettlement')}
              value={summary?.liquidity_planning.projected_next_settlement_to_withdrawable_cents || 0}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Statistic
              title={t('liquidity.totalCashNeeded')}
              value={(summary?.liquidity_planning.approved_pending_withdrawals_cents || 0) + (summary?.liquidity_planning.projected_next_settlement_to_withdrawable_cents || 0)}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<DollarOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Statistic
              title={
                <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
                  {t('liquidity.suggestedTransfer')}
                </span>
              }
              value={summary?.liquidity_planning.suggested_transfer_to_bank_cents || 0}
              formatter={(value) => {
                const amount = Number(value);
                return `${amount > 0 ? '+' : ''}${formatCurrency(amount)}`;
              }}
              prefix={<BankOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: getSuggestedTransferColor(), fontWeight: 'bold', fontSize: 24 }}
            />
            <Text style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
              {summary && summary.liquidity_planning.suggested_transfer_to_bank_cents > 0
                ? t('liquidity.transferIn')
                : summary && summary.liquidity_planning.suggested_transfer_to_bank_cents < 0
                ? t('liquidity.transferOut')
                : t('liquidity.noTransfer')}
            </Text>
          </Col>
        </Row>
      </Card>

      {/* Company Ledger Table */}
      <Card
        title={t('ledger.title')}
        extra={
          <RangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            format="YYYY-MM-DD"
          />
        }
        style={{ marginBottom: 24 }}
      >
        <Table
          columns={ledgerColumns}
          dataSource={ledgerEntries}
          loading={entriesLoading}
          pagination={pagination}
          onChange={handleTableChange}
          rowKey="id"
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Pool Composition Table */}
      <Card title={t('pool.title')} style={{ marginBottom: 24 }} loading={loading}>
        <Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>
          {t('pool.description')}
        </Text>
        <Table
          columns={poolColumns}
          dataSource={summary?.pool_composition || []}
          pagination={false}
          rowKey="user_id"
          scroll={{ x: 800 }}
        />
      </Card>

      {/* Manual Adjustment Modal */}
      <Modal
        title={t('adjustment.modalTitle')}
        open={adjustmentModalVisible}
        onCancel={() => {
          form.resetFields();
          setAdjustmentModalVisible(false);
        }}
        onOk={() => form.submit()}
        okText={t('adjustment.submit')}
        cancelText={t('adjustment.cancel')}
      >
        <Form form={form} onFinish={handleCreateAdjustment} layout="vertical">
          <Form.Item
            name="kind"
            label={t('adjustment.kind')}
            rules={[{ required: true, message: t('adjustment.kindRequired') }]}
          >
            <Select>
              <Select.Option value="manual_adjustment">
                {t('ledger.kinds.manual_adjustment')}
              </Select.Option>
              <Select.Option value="correction">
                {t('ledger.kinds.correction')}
              </Select.Option>
              <Select.Option value="fee_adjustment">
                {t('ledger.kinds.fee_adjustment')}
              </Select.Option>
              <Select.Option value="bank_transfer_in">
                {t('ledger.kinds.bank_transfer_in')}
              </Select.Option>
              <Select.Option value="bank_transfer_out">
                {t('ledger.kinds.bank_transfer_out')}
              </Select.Option>
              <Select.Option value="company_sweep">
                {t('ledger.kinds.company_sweep')}
              </Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="amount_cents"
            label={t('adjustment.amount')}
            rules={[{ required: true, message: t('adjustment.amountRequired') }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
              step={0.01}
              precision={2}
            />
          </Form.Item>
          <Form.Item
            name="description"
            label={t('adjustment.description')}
            rules={[{ required: true, message: t('adjustment.descriptionRequired') }]}
          >
            <TextArea rows={3} placeholder={t('adjustment.descriptionPlaceholder')} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default AdminTreasury;
